import React from "react";
import { ISchemaProperty } from "../../Schema";
import { PropertyTypes } from "../../../../../enums/PropertyTypes";

interface IFormAutoFill {
    props: ISchemaProperty[];
    values: any;
    isCreate: boolean;
    parentValues?: Record<string, any> | undefined;
}

// Map of field names to standard browser autofill names
// Values can be strings or functions that take autocomplete and return the standard name
// Note: Year fields (yearStart, yearEnd, etc.) are NOT mapped so browsers can learn from the original field names
const autofillFieldNameMap: Record<string, string | ((autocomplete?: string) => string)> = {
    'telephone': 'phone',
    'mobile': 'phone',
    'phoneNumber': 'phone',
    'address1': 'address-line1',
    'address2': 'address-line2',
    'city': 'address-level2',
    'county': 'address-level1',
    'zip': 'postal-code',
    'name': (autocomplete?: string) => autocomplete === 'organization' ? 'company' : 'name',
};

// Helper function to get standard browser autofill field name
function getAutofillFieldName(propName: string, autocomplete?: string): string {
    const mapping = autofillFieldNameMap[propName];
    if (!mapping) return propName;
    if (typeof mapping === 'function') {
        return mapping(autocomplete);
    }
    return mapping;
}

function getInputType(autocomplete: string, inputType?: string, propertyType?: string): string {
    if (inputType) return inputType;
    
    // Use autocomplete value directly for email/tel
    if (autocomplete === 'email' || autocomplete === 'tel') {
        return autocomplete;
    }
    
    // Determine input type based on property type
    // Note: Use 'text' for all fields in hidden form (except dates) for better autofill support
    // Browsers autofill text inputs much better than number inputs
    switch (propertyType) {
        case PropertyTypes.dateOnly:
            return 'date';
        default:
            // Use text for all other types (number, year, etc.) for better autofill
            return 'text';
    }
}

function getElementType(propertyType?: string): 'input' | 'textarea' {
    if (propertyType === PropertyTypes.textArea) {
        return 'textarea';
    }
    return 'input';
}

function collectAutofillFields(props: ISchemaProperty[], values: any, isCreate: boolean, parentValues?: Record<string, any>): JSX.Element[] {
    const fields: JSX.Element[] = [];
    
    props.forEach(prop => {
        if (!prop.showInForm) return;
        if (prop.addOnly === true && !isCreate) return;
        if (prop.editOnly === true && isCreate) return;
        if (prop.renderBasedOnParentData && parentValues && !prop.renderBasedOnParentData(parentValues, isCreate)) return;
        
        if (prop.children && prop.children.length > 0) {
            const parentValue = values[prop.name] || {};
            prop.children.forEach(childProp => {
                if (childProp.autocomplete) {
                    const fieldValue = parentValue[childProp.name] || '';
                    const inputType = getInputType(childProp.autocomplete, childProp.inputType, childProp.type);
                    const autofillName = getAutofillFieldName(childProp.name, childProp.autocomplete);
                    const elementType = getElementType(childProp.type);
                    
                    if (elementType === 'textarea') {
                        fields.push(
                            <textarea
                                key={`${prop.name}.${childProp.name}`}
                                name={autofillName}
                                autoComplete={childProp.autocomplete}
                                defaultValue={fieldValue}
                                data-field-key={`${prop.name}.${childProp.name}`}
                                style={{ display: 'none' }}
                            />
                        );
                    } else {
                        fields.push(
                            <input
                                key={`${prop.name}.${childProp.name}`}
                                type={inputType}
                                name={autofillName}
                                autoComplete={childProp.autocomplete}
                                defaultValue={fieldValue}
                                data-field-key={`${prop.name}.${childProp.name}`}
                                style={{ display: 'none' }}
                            />
                        );
                    }
                }
            });
        }
        
        // Add top-level field if it has autocomplete
        // Support string, textArea, and dateOnly for autofill (not number/year/quantity)
        if (prop.autocomplete && (
            prop.type === PropertyTypes.string ||
            prop.type === PropertyTypes.textArea ||
            prop.type === PropertyTypes.dateOnly
        )) {
            const fieldValue = values[prop.name] || '';
            const inputType = getInputType(prop.autocomplete, prop.inputType, prop.type);
            const autofillName = getAutofillFieldName(prop.name, prop.autocomplete);
            const elementType = getElementType(prop.type);
            
            if (elementType === 'textarea') {
                fields.push(
                    <textarea
                        key={prop.name}
                        name={autofillName}
                        autoComplete={prop.autocomplete}
                        defaultValue={fieldValue}
                        data-field-key={prop.name}
                        style={{ display: 'none' }}
                    />
                );
            } else {
                fields.push(
                    <input
                        key={prop.name}
                        type={inputType}
                        name={autofillName}
                        autoComplete={prop.autocomplete}
                        defaultValue={fieldValue}
                        data-field-key={prop.name}
                        style={{ display: 'none' }}
                    />
                );
            }
        }
    });
    
    return fields;
}

export default function FormAutoFill({ props, values, isCreate, parentValues }: IFormAutoFill) {
    const autofillFields = collectAutofillFields(props, values, isCreate, parentValues);

    return (
        <>
            {/* Hidden iframe for real form submission to teach autofill */}
            <iframe name="autofill_target" style={{ display: 'none' }} />
            
            {/* Hidden real HTML form for autofill learning - posts to about:blank so iframe loads without hitting server (no 404) */}
            <form
                id="autofill-form"
                action="about:blank"
                method="post"
                target="autofill_target"
                style={{ display: 'none' }}
            >
                {autofillFields}
            </form>
        </>
    );
}
